import type { ReactNode } from 'react';
import { DASH } from '../lib/format';
import type { ChipTone } from './Chip';
import { Icon } from './Icon';

interface Props {
  cve: string | null;
  tone?: ChipTone;
  source?: string | null;
  links?: readonly { label: string; url: string }[];
}

/** Native links preserve keyboard/new-tab behavior without expanding table rows. */
export default function CveLink({ cve, tone = 'alert', source, links }: Props): ReactNode {
  if (!cve) return <span className="muted">{DASH}</span>;
  const safeLinks = links?.filter((link) => /^https?:\/\//i.test(link.url)) ?? [];
  const targets = safeLinks.length ? safeLinks : [{
    label: source || 'Quelle',
    url: `/cve/${encodeURIComponent(cve)}/source${source ? `?source=${encodeURIComponent(source)}` : ''}`,
  }];
  const toneClass = { neutral: '', good: 'ok', warn: 'warn', alert: 'kev', info: 'info', idle: 'idle', accent: 'eu' }[tone];
  return <>{targets.map((link, index) => (
    <a key={link.url} className={`chip mono ${toneClass}`} href={link.url}
      target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}
      title={`${link.label} öffnen`}>
      {index === 0 ? cve : link.label}
      <Icon name="external" size={12} />
    </a>
  ))}</>;
}

/** Link CVE identifiers in readable text while retaining surrounding prose. */
export function CveText({ text }: { text: string }): ReactNode {
  return <>{text.split(/(CVE-\d{4}-\d{4,})/gi).map((part, index) =>
    /^CVE-\d{4}-\d{4,}$/i.test(part)
      ? <CveLink key={index} cve={part} />
      : part,
  )}</>;
}
